/**
 * Gwynn's view of the packet eMerge prepared itself (P0, Isaiah Cotto,
 * 2026-10-07): where it stands, what holds it and who resolves that, and the
 * three acts left to her -- preview it, download it for upload (which dates its
 * cover), and confirm she uploaded it to DCF. Plus the DCF profile: its state,
 * and the case number and Medicaid PIN she records there, which update the
 * cover by themselves.
 *
 * Every state is said in words with a text marker, never by colour alone.
 * Nothing here uploads to DCF or claims to: the upload is hers, in the DCF
 * portal, and the confirmation is recorded as her attestation.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { umedApi, shortDate, shortDateTime, freshKey, PACKET_BLOCKER_OWNER_LABEL, DCF_NEED_LABEL } from './umedApi';
import type { PacketPreparationStatus, PacketDownload, DcfNeed } from './umedApi';

type Outcome = { success: boolean; error?: string; message?: string; code?: string; status?: number };
const said = (r: Outcome): string => {
  if (r.status === undefined) return 'Could not reach the server. Nothing was recorded.';
  if (r.status >= 500) return 'The server failed. Nothing was recorded, and it is not a permissions problem.';
  return r.error || r.message || (r.code ? `Refused (${r.code}).` : 'The server refused this.');
};

/** A marker that reads without colour. */
const MARKER: Record<string, string> = { READY: '✓', SUBMITTED: '✓', PREPARING: '…', ACTION_REQUIRED: '!', FAILED: '✕', WITHDRAWN: '–' };

const WITHDRAWN_WORDS: Record<string, string> = {
  PA_WITHDREW_READINESS: 'The Patient Advocate took this case back from Ready to submit.',
  CASE_CLOSED: 'The case is closed.',
  RESIDENT_DECEASED: 'The resident is recorded as deceased.',
  RESIDENT_NO_LONGER_QUALIFIES: 'The resident is recorded as no longer qualifying.',
  RESIDENT_NEEDS_RECERT: 'The resident needs Medicaid recertification.',
  PAST_SUBMISSION: 'The case is already at UMED Submitted or later.',
  NOT_NATIVE: 'The Monday board still moves this case.',
};

/** Today in Florida, as the server reads "uploaded on" (YYYY-MM-DD). */
const floridaToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
const usDay = (iso: string) => (iso ? `${iso.slice(5, 7)}/${iso.slice(8, 10)}/${iso.slice(0, 4)}` : '');

/**
 * A case eMerge moves forward that has no automatic preparation yet (one opened
 * before this shipped): one button asks for it. The manual generator stays
 * beside it until it is used.
 */
export function PreparePacketButton({ caseId, onChanged }: { caseId: string; onChanged: (msg: string) => void | Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const run = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await umedApi.preparePacket(caseId);
      if (!r.success || !r.data) { setErr(said(r)); return; }
      await onChanged(r.data.outcome === 'READY' || r.data.outcome === 'CURRENT' ? `Packet prepared (revision ${r.data.version}).` : 'Preparation requested: see what holds it.');
    } finally { setBusy(false); }
  };
  return (
    <div className="btns">
      <button type="button" className="um-open" disabled={busy} onClick={run}>{busy ? 'Preparing…' : 'Prepare the packet automatically'}</button>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
    </div>
  );
}

export default function PacketPreparationPanel({
  caseId, canGenerate, canSubmit, canView, dcfNeed, dcfState, submissionRecorded = false, onChanged,
}: {
  caseId: string; canGenerate: boolean; canSubmit: boolean; canView: boolean;
  /** The case already has a sending for what it is sending now (#368 round 2): nothing is offered to send it again. */
  submissionRecorded?: boolean;
  dcfNeed?: DcfNeed | null; dcfState?: string | null;
  onChanged: (msg: string) => void | Promise<void>;
}) {
  const [s, setS] = useState<PacketPreparationStatus | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [downloaded, setDownloaded] = useState<PacketDownload | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [attest, setAttest] = useState(false);
  const [uploadedOn, setUploadedOn] = useState(floridaToday());
  const [dateAttest, setDateAttest] = useState(false);
  const [reference, setReference] = useState('');
  const [caseNumber, setCaseNumber] = useState('');
  const [pin, setPin] = useState('');
  const [previewedRevision, setPreviewedRevision] = useState<string | null>(null);
  const [completeAck, setCompleteAck] = useState(false);
  const [warningsAck, setWarningsAck] = useState(false);
  const confirmKey = useRef<string>(freshKey());

  const load = useCallback(async () => {
    setFailed(false);
    try {
      const r = await umedApi.packetPreparation(caseId);
      if (r.success && r.data) setS(r.data); else setFailed(true);
    } catch { setFailed(true); }
  }, [caseId]);
  useEffect(() => { setS(null); setDownloaded(null); setConfirming(false); setAttest(false); setErr(null); load(); }, [load]);
  useEffect(() => {
    setPreviewedRevision(null); setCompleteAck(false); setWarningsAck(false);
    setConfirming(false); setAttest(false); setDateAttest(false);
  }, [caseId, s?.current?.packet_id, s?.current?.sha256, s?.live.stale, s?.review?.complete]);

  const act = async (name: string, fn: () => Promise<Outcome & { data?: any }>, done: (data: any) => string | null) => {
    setBusy(name); setErr(null);
    try {
      const r = await fn();
      if (!r.success) { setErr(said(r)); await load(); return null; }
      const msg = done(r.data);
      await load();
      if (msg) await onChanged(msg);
      return r.data;
    } finally { setBusy(null); }
  };

  if (failed) return <p className="um-quiet" role="status">The prepared packet could not be loaded. <button type="button" className="um-link" onClick={load}>Try again</button></p>;
  if (!s) return <p className="um-quiet" role="status">Loading the prepared packet…</p>;

  const cur = s.current;
  const stale = s.live.stale;
  const withdrawn = s.live.withdrawn;
  const usable = s.upload_ready && cur !== null && !submissionRecorded && !s.live.submitted;
  const sendingReady = usable && s.sending_ready === true && s.review?.complete === true;
  const dated = (downloaded && cur && downloaded.packetId === cur.packet_id && downloaded.sha256 === cur.sha256 ? downloaded : null)
    || (cur && cur.revision_kind === 'FINAL' ? { packetId: cur.packet_id, version: cur.version, sha256: cur.sha256, pages: cur.pages, dateSubmitted: cur.date_submitted || '' } : null);
  const stateKey = s.state || 'NONE';

  const preview = () => act('preview', () => umedApi.documentUrl(caseId, cur!.document_id), (d) => {
    window.open(d.url, '_blank', 'noopener,noreferrer'); setPreviewedRevision(`${cur!.packet_id}:${cur!.sha256}`); return null;
  });
  const download = () => act('download', () => umedApi.downloadPacket(caseId), (d: PacketDownload) => {
    setDownloaded(d);
    window.open(d.url, '_blank', 'noopener,noreferrer');
    return null;
  });
  const prepare = () => act('prepare', () => umedApi.preparePacket(caseId), (d) => (d.outcome === 'READY' || d.outcome === 'CURRENT' ? `Packet prepared (revision ${d.version}).` : null));
  const review = () => act('review', () => umedApi.reviewPacket(caseId, {
    packet_id: cur!.packet_id, sha256: cur!.sha256, complete: true, warnings_acknowledged: true,
  }), (d) => d.accountReady ? 'Exact packet revision reviewed complete.' : 'Packet reviewed. DCF account still required before sending.');
  const regenerate = () => act('regenerate', () => umedApi.regenerateCorrectionPacket(caseId, {
    packet_id: cur!.packet_id, sha256: cur!.sha256,
  }), () => { setDownloaded(null); return 'Correction regeneration requested. The new dated revision requires review.'; });
  const identifiers = () => act('identifiers', () => umedApi.recordPacketIdentifiers(caseId, {
    ...(caseNumber.trim() ? { dcf_case_number: caseNumber.trim() } : {}), ...(pin.trim() ? { medicaid_pin: pin.trim() } : {}),
  }), (d) => { setCaseNumber(''); setPin(''); return d.recorded.length ? 'Recorded. The cover sheet was updated with it.' : 'Nothing changed: those values were already recorded.'; });
  const coverDay = dated ? dated.dateSubmitted : '';
  const otherDay = Boolean(dated && coverDay && uploadedOn && uploadedOn !== coverDay);
  const dateLine = otherDay ? `I uploaded the revision dated ${usDay(coverDay)} on ${usDay(uploadedOn)}` : null;
  const confirm = () => act('confirm', () => umedApi.confirmPacketUpload(caseId, {
    packet_id: dated!.packetId, sha256: dated!.sha256, attested: true, uploaded_on: uploadedOn, ...(otherDay ? { date_attested: dateAttest } : {}),
    ...(reference.trim() ? { reference: reference.trim() } : {}), idempotency_key: confirmKey.current,
  }), () => { setConfirming(false); confirmKey.current = freshKey(); return 'Upload confirmed. The case is now UMED Submitted, with Josie.'; });

  return (
    <div className={`um-prep um-prep-${stateKey.toLowerCase()}`} aria-label="Prepared packet">
      <p className="um-prep-state" role="status">
        <span className="um-prep-marker" aria-hidden="true">{MARKER[stateKey] || '·'}</span>{' '}
        <strong>{stale && s.state === 'READY' ? 'Packet out of date' : withdrawn && s.state === 'READY' ? 'Withdrawn' : s.label}</strong>
        {s.reason_label && <> · {s.reason_label}</>}
        {cur && <> · revision {cur.version} · {cur.pages} page{cur.pages === 1 ? '' : 's'} including the cover</>}
        {s.ready_at && s.state === 'READY' && <> · prepared {shortDateTime(s.ready_at)}</>}
      </p>

      {s.state === null && <p className="um-quiet">No packet has been prepared for this case. It is prepared by itself when a Patient Advocate marks the case Ready to submit.</p>}
      {s.state === 'PREPARING' && <p className="um-quiet">eMerge is preparing the cover sheet and the packet. Nothing to do yet.</p>}
      {s.state === 'ACTION_REQUIRED' && (
        <div>
          <p>The packet is held until this is resolved:</p>
          <ul className="um-blockers" aria-label="What holds the packet">
            {s.blockers.map((b) => <li key={b.code}>{b.message} <span className="um-quiet">Resolved by {PACKET_BLOCKER_OWNER_LABEL[b.owner] || b.owner}.</span></li>)}
          </ul>
          <p className="um-quiet">It is prepared again by itself once this is resolved.</p>
        </div>
      )}
      {s.state === 'FAILED' && <p>The packet could not be generated ({s.last_error_code}). Nothing was published. Retry it once the cause is fixed.</p>}
      {(s.state === 'WITHDRAWN' || withdrawn) && <p>{WITHDRAWN_WORDS[s.withdrawn_reason || withdrawn || ''] || 'This case was withdrawn.'} The packet is not to be sent.</p>}
      {stale && !withdrawn && s.state === 'READY' && <p>What the packet is made from has changed since revision {cur?.version}. It is being prepared again; do not upload the old one.</p>}
      {s.state === 'SUBMITTED' && s.submission && (
        <p>Submitted to DCF on {shortDate(s.submission.submitted_on)}{s.submission.reference ? ` · reference ${s.submission.reference}` : ''} · confirmed by the operator (attestation, not an automated receipt). Now with Josie in UMED Submitted.</p>
      )}

      {/* The DCF profile: Gwynn's own next step when it is still needed, never a reason the packet is withheld from her. */}
      <dl className="um-kv">
        <dt>DCF profile</dt>
        <dd>{dcfNeed ? DCF_NEED_LABEL[dcfNeed] || dcfNeed : 'Unknown'}{dcfState ? ` (${dcfState.replace(/_/g, ' ').toLowerCase()})` : ''}{dcfNeed === 'NEEDED' ? ' — next: create or link the DCF profile, then upload.' : ''}</dd>
        <dt>DCF case #</dt>
        <dd>{s.identifiers.dcf_case_number.recorded ? (s.identifiers.dcf_case_number.value || 'Recorded') : <span className="um-quiet">Not recorded yet</span>}</dd>
        <dt>Medicaid PIN</dt>
        <dd>{s.identifiers.medicaid_pin.recorded ? (s.identifiers.medicaid_pin.value || 'Recorded') : <span className="um-quiet">Not recorded yet</span>}</dd>
      </dl>
      {canSubmit && !submissionRecorded && s.state !== 'SUBMITTED' && s.state !== null && (
        <div className="um-form" aria-label="DCF profile identifiers">
          <label><span>DCF case #</span><input value={caseNumber} onChange={(e) => setCaseNumber(e.target.value)} maxLength={40} aria-label="DCF case number" /></label>
          <label><span>Medicaid PIN</span><input value={pin} onChange={(e) => setPin(e.target.value)} maxLength={40} aria-label="Medicaid PIN" /></label>
          <div className="btns"><button type="button" className="um-open" disabled={busy !== null || (!caseNumber.trim() && !pin.trim())} onClick={identifiers}>{busy === 'identifiers' ? 'Recording…' : 'Record from the DCF profile'}</button></div>
          <p className="um-quiet">Recorded once; the cover sheet updates itself. The rest of the packet is unchanged.</p>
        </div>
      )}

      <div className="btns um-prep-actions">
        {canView && cur && s.state !== 'SUBMITTED' && <button type="button" className="um-open" disabled={busy !== null} onClick={preview}>{busy === 'preview' ? 'Opening…' : 'Preview packet'}</button>}
        {canView && canGenerate && usable && <button type="button" className="um-open um-primary" disabled={busy !== null} onClick={download}>{busy === 'download' ? 'Preparing the download…' : 'Download submission packet'}</button>}
        {canSubmit && usable && <button type="button" className="um-open" disabled={busy !== null || !dated || !sendingReady} onClick={() => setConfirming(true)} aria-expanded={confirming}>Confirm DCF upload</button>}
        {canGenerate && !submissionRecorded && s.can_regenerate_correction && cur && <button type="button" className="um-open" disabled={busy !== null} onClick={regenerate}>{busy === 'regenerate' ? 'Regenerating...' : 'Regenerate correction packet'}</button>}
        {canGenerate && !submissionRecorded && (s.state === 'FAILED' || s.state === null || (s.state === 'ACTION_REQUIRED')) && <button type="button" className="um-open" disabled={busy !== null} onClick={prepare}>{busy === 'prepare' ? 'Preparing…' : s.state === 'FAILED' ? 'Retry' : 'Prepare packet'}</button>}
      </div>
      {usable && s.review?.complete && <p role="status">Revision {cur!.version} reviewed complete {s.review.reviewed_at ? shortDateTime(s.review.reviewed_at) : ''}.</p>}
      {canSubmit && canView && usable && dated && !s.review?.complete && (
        <div className="um-form" role="group" aria-label="Packet completeness review">
          <p>Revision {cur!.version} · fingerprint {cur!.sha256.slice(0, 12)}</p>
          {s.live.warnings.length > 0 && <ul aria-label="Packet warnings">{s.live.warnings.map((warning) => <li key={warning.field}>{warning.why}</li>)}</ul>}
          <label className="um-check"><input type="checkbox" checked={completeAck} onChange={(event) => setCompleteAck(event.target.checked)} aria-label="Packet is complete" />
            <span>I reviewed this exact dated revision and its attachments and acknowledge the packet is complete.</span></label>
          <label className="um-check"><input type="checkbox" checked={warningsAck} onChange={(event) => setWarningsAck(event.target.checked)} aria-label="Acknowledge packet warnings" />
            <span>I acknowledge all listed warnings{s.live.warnings.length === 0 ? ' (none)' : ''}.</span></label>
          <button type="button" className="um-open um-primary" disabled={busy !== null || !completeAck || !warningsAck || previewedRevision !== `${cur!.packet_id}:${cur!.sha256}`} onClick={review}>
            {busy === 'review' ? 'Recording review...' : 'Acknowledge complete packet'}
          </button>
        </div>
      )}
      {canSubmit && usable && !dated && <p className="um-quiet">Download the submission packet first: downloading dates its cover sheet for today and names you as the sender.</p>}
      {dated && usable && <p className="um-quiet">Downloaded: revision {dated.version}, dated {dated.dateSubmitted ? shortDate(dated.dateSubmitted) : 'for upload'}. Upload that file to DCF ACCESS, then confirm.</p>}

      {confirming && dated && sendingReady && (
        <div className="um-form" role="group" aria-label="Confirm the DCF upload">
          <label className="um-check"><input type="checkbox" checked={attest} onChange={(e) => setAttest(e.target.checked)} aria-label="I uploaded this packet" />
            <span>I uploaded revision {dated.version} of this packet (fingerprint {dated.sha256.slice(0, 12)}…) to DCF ACCESS.</span></label>
          <label><span>Uploaded on</span><input type="date" value={uploadedOn} max={floridaToday()} onChange={(e) => { setUploadedOn(e.target.value); setDateAttest(false); }} aria-label="Uploaded on" /></label>
          {dateLine && (
            <label className="um-check"><input type="checkbox" checked={dateAttest} onChange={(e) => setDateAttest(e.target.checked)} aria-label="Upload date differs from the cover" />
              <span>{dateLine}.</span></label>
          )}
          <label><span>DCF confirmation or reference (if shown)</span><input value={reference} onChange={(e) => setReference(e.target.value)} maxLength={200} aria-label="DCF reference" /></label>
          <div className="btns">
            <button type="button" className="um-open um-primary" disabled={busy !== null || !attest || !uploadedOn || (otherDay && !dateAttest)} onClick={confirm}>{busy === 'confirm' ? 'Recording…' : 'Confirm upload'}</button>
            <button type="button" className="um-open" disabled={busy !== null} onClick={() => setConfirming(false)}>Cancel</button>
          </div>
          <p className="um-quiet">Recorded as your confirmation, not as a receipt from DCF. The case moves to UMED Submitted for Josie.</p>
        </div>
      )}
      {err && <p className="um-notice stop" role="alert">{err}</p>}
    </div>
  );
}
